import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import AuthService from "../../services/auth.service";
import "./VerifyEmail.css";

// Inline SVG icons for a cleaner bundle
const CheckIcon = () => (
  <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ErrorIcon = () => (
  <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="15" y1="9" x2="9" y2="15" />
    <line x1="9" y1="9" x2="15" y2="15" />
  </svg>
);

const SpinnerIcon = () => (
  <svg className="verify-spinner" width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#0ea5e9" strokeWidth="2.5" strokeLinecap="round">
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
);

const MailIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const STATUS = {
  LOADING: "loading",
  SUCCESS: "success",
  ERROR: "error",
};

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("verification_token");

  const [status, setStatus] = useState(STATUS.LOADING);
  const [errorMessage, setErrorMessage] = useState("");
  const [countdown, setCountdown] = useState(5);

  // Resend state
  const [email, setEmail] = useState("");
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState("");

  // Verify email on mount
  useEffect(() => {
    if (!token) {
      setStatus(STATUS.ERROR);
      setErrorMessage("No verification token found in the URL.");
      return;
    }

    const verifyEmail = async () => {
      try {
        const response = await AuthService.verifyEmail(token);
        setStatus(STATUS.SUCCESS);
      } catch (error) {
        setStatus(STATUS.ERROR);
        const msg =
          error.response?.data?.message ||
          "Invalid or expired verification link.";
        setErrorMessage(msg);
      }
    };

    verifyEmail();
  }, [token]);

  // Auto-redirect countdown after success
  useEffect(() => {
    if (status !== STATUS.SUCCESS) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate("/login");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [status, navigate]);

  // Resend handler
  const handleResend = useCallback(async () => {
    if (!email || !email.includes("@")) {
      setResendError("Please enter a valid email address.");
      return;
    }

    setResending(true);
    setResendError("");
    setResendSuccess(false);

    try {
      await AuthService.resendVerification({ email });
      setResendSuccess(true);
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        "Failed to resend verification email. Please try again.";
      setResendError(msg);
    } finally {
      setResending(false);
    }
  }, [email]);

  return (
    <div className="verify-email-page">
      <Helmet>
        <title>Verify Email | Carib Bargains</title>
        <meta name="description" content="Verify your Carib Bargains email address" />
      </Helmet>

      <div className="verify-card">
        {/* Logo */}
        <img
          src="/img/logo.png"
          alt="Carib Bargains"
          className="verify-logo"
        />

        {/* ===== LOADING STATE ===== */}
        {status === STATUS.LOADING && (
          <>
            <div className="verify-icon-wrapper loading">
              <SpinnerIcon />
            </div>
            <h1 className="verify-title">Verifying your email...</h1>
            <p className="verify-subtitle">
              Please wait while we confirm your email address.
            </p>
          </>
        )}

        {/* ===== SUCCESS STATE ===== */}
        {status === STATUS.SUCCESS && (
          <>
            <div className="verify-icon-wrapper success">
              <CheckIcon />
            </div>
            <h1 className="verify-title">Email Verified!</h1>
            <p className="verify-subtitle">
              Your email has been verified successfully.
              <br />
              You can now log in and start using Carib Bargains.
            </p>
            <button
              className="verify-btn-primary go-login"
              onClick={() => navigate("/login")}
            >
              Go to Login
            </button>
            <p className="countdown-text">
              Redirecting in <span>{countdown}s</span>
            </p>
            <p className="verify-redirect-text">
              If not redirected, click the button above.
            </p>
          </>
        )}

        {/* ===== ERROR STATE ===== */}
        {status === STATUS.ERROR && (
          <>
            <div className="verify-icon-wrapper error">
              <ErrorIcon />
            </div>
            <h1 className="verify-title">Verification Failed</h1>
            <p className="verify-subtitle">{errorMessage}</p>

            <div className="resend-section">
              <p className="verify-subtitle" style={{ marginBottom: 12, fontSize: '0.88rem' }}>
                Enter your email to receive a new verification link:
              </p>
              <div className="resend-input-group">
                <input
                  type="email"
                  className="resend-email-input"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setResendError("");
                  }}
                  disabled={resending}
                />
              </div>
              {resendError && (
                <p style={{ color: '#dc2626', fontSize: '0.82rem', marginBottom: 8 }}>
                  {resendError}
                </p>
              )}
              <button
                className="verify-btn-primary resend"
                onClick={handleResend}
                disabled={resending}
              >
                {resending ? "Sending..." : "Resend Verification Email"}
              </button>
              {resendSuccess && (
                <div className="resend-success-msg">
                  <MailIcon />
                  A new verification link has been sent to your email.
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
